import React from 'react';
import * as BizCharts from 'bizcharts';
import {
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_PREVIOUS,
} from '../../utils/AnalyticsComparisonUtils';

const { Chart, Axis, Geom, Legend, Tooltip } = BizCharts as any;

const AXIS_Y_LINE_COLOR = '#D9D9D9';
const AXIS_X_LINE_COLOR = '#E8E8E8';
const DEFAULT_HEIGHT = 360;
const DEFAULT_BAR_SIZE = 40;

export type SpendBarChartProps = {
	data: any[];
	xField?: string;
	yField?: string;
	hasComparison: boolean;
	currentColor: string;
	previousColor: string;
	axisXTitle?: string;
	axisYTitle: string;
	formatYAxisLabel: (val: any) => string;
	/** Tooltip definition for the interval geom in single mode (BizCharts `[fields, formatter]`). */
	intervalTooltipDefinition?: [string, (...args: any[]) => { name: string; value: string }];
	/** Custom content for the comparison-mode tooltip (Tooltip.customContent). */
	renderComparisonTooltip?: (title: string, items: any[]) => string;
	/** Click handler for bars in single (non-comparison) mode. */
	onIntervalClick?: (ev: any) => void;
	barSize?: number;
	height?: number;
	/**
	 * Restrict the category-axis range to a fraction of the plot width. Used to keep the
	 * intra-pair gap near a target pixel width when fewer pairs are displayed. Pass `undefined`
	 * to use the full width.
	 */
	monthLabelRange?: [number, number];
};

const COMPARISON_TOOLTIP_DOM_STYLES = {
	'g2-tooltip': {
		padding: '0',
		boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
		borderRadius: '6px',
		background: '#fff',
	},
	'g2-tooltip-list': {
		margin: '0',
		padding: '0',
	},
} as const;

const SINGLE_TOOLTIP_DOM_STYLES = {
	'g2-tooltip': {
		padding: '8px 10px',
		boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
		borderRadius: '6px',
		background: 'rgba(255,255,255,0.96)',
	},
	'g2-tooltip-list': {
		margin: '0',
		padding: '0',
	},
} as const;

const SINGLE_ITEM_TPL =
	'<li data-index={index} style="list-style:none;padding:0;margin:0;">{value}</li>';

const ROTATED_LABEL = {
	autoRotate: false,
	rotate: -Math.PI / 4,
	offset: 18,
	style: {
		fontSize: 12,
		fill: 'rgba(0,0,0,0.45)',
		textAlign: 'end',
		textBaseline: 'middle',
	},
} as const;

const SpendBarChart = ({
	data,
	xField = 'monthLabel',
	yField = 'spendValue',
	hasComparison,
	currentColor,
	previousColor,
	axisXTitle = 'Month',
	axisYTitle,
	formatYAxisLabel,
	intervalTooltipDefinition,
	renderComparisonTooltip,
	onIntervalClick,
	barSize = DEFAULT_BAR_SIZE,
	height = DEFAULT_HEIGHT,
	monthLabelRange,
}: SpendBarChartProps) => {
	const seriesColor = (seriesValue: any) =>
		seriesValue === COMPARISON_SERIES_PREVIOUS ? previousColor : currentColor;

	const scale: any = {
		[yField]: {
			min: 0,
			nice: true,
		},
		[COMPARISON_SERIES_FIELD]: {
			type: 'cat',
			values: [COMPARISON_SERIES_PREVIOUS, COMPARISON_SERIES_CURRENT],
		},
		[xField]: monthLabelRange ? { type: 'cat', range: monthLabelRange } : { type: 'cat' },
	};

	return (
		<Chart
			height={height}
			data={data}
			autoFit={true}
			forceFit={true}
			theme="openwrench"
			scale={scale}
			onIntervalClick={hasComparison ? undefined : onIntervalClick}
		>
			<Axis
				name={xField}
				title={{
					text: axisXTitle,
					style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
					offset: 70,
				}}
				line={{ style: { stroke: AXIS_X_LINE_COLOR } }}
				tickLine={null}
				label={hasComparison ? null : ROTATED_LABEL}
				grid={{
					line: {
						style: {
							stroke: AXIS_Y_LINE_COLOR,
							lineDash: [4, 4],
						},
					},
				}}
			/>
			<Axis
				name={yField}
				title={{
					text: axisYTitle,
					style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
					offset: 80,
				}}
				label={{
					formatter: formatYAxisLabel,
					style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
				}}
				line={{ style: { stroke: AXIS_Y_LINE_COLOR } }}
				tickLine={null}
				grid={{
					line: {
						style: {
							stroke: AXIS_Y_LINE_COLOR,
							lineDash: [4, 4],
						},
					},
				}}
			/>
			<Legend visible={false} />
			{hasComparison ? (
				<Tooltip
					showTitle={false}
					shared={true}
					enterable={true}
					crosshairs={{ type: 'x' }}
					customContent={renderComparisonTooltip}
					domStyles={COMPARISON_TOOLTIP_DOM_STYLES}
				/>
			) : (
				<Tooltip
					showTitle={false}
					enterable={true}
					crosshairs={{ type: 'y' }}
					itemTpl={SINGLE_ITEM_TPL}
					domStyles={SINGLE_TOOLTIP_DOM_STYLES}
				/>
			)}
			{hasComparison ? (
				<Geom
					key="bar-cmp"
					type="interval"
					size={barSize}
					position={`${xField}*${yField}`}
					color={[COMPARISON_SERIES_FIELD, seriesColor]}
					adjust={[{ type: 'dodge', marginRatio: 0.2 }]}
					style={{ cursor: 'default' }}
					tooltip={intervalTooltipDefinition}
					label={[
						xField,
						{
							position: 'bottom',
							offset: -12,
							autoRotate: false,
							rotate: -Math.PI / 4,
							content: (rowData: any) =>
								rowData[COMPARISON_SERIES_FIELD] === COMPARISON_SERIES_PREVIOUS
									? rowData.previousMonthLabel
									: rowData[xField],
							style: {
								fontSize: 12,
								fill: 'rgba(0,0,0,0.45)',
								textAlign: 'end',
								textBaseline: 'middle',
							},
						},
					]}
				/>
			) : (
				<Geom
					key="bar-single"
					type="interval"
					size={barSize}
					position={`${xField}*${yField}`}
					color={currentColor}
					style={{ cursor: onIntervalClick ? 'pointer' : 'default' }}
					tooltip={intervalTooltipDefinition}
				/>
			)}
		</Chart>
	);
};

export default SpendBarChart;
